import React, { useMemo, useCallback, useRef } from 'react';
import { GeoJSON, useMap, CircleMarker, Tooltip } from 'react-leaflet';
import { RoutePlanAlternative } from '../../types';
import L from 'leaflet';

import { BRAND } from '../../constants/mapColors';
interface RoutePlanPreviewProps {
  alternatives: RoutePlanAlternative[];
  selectedIndex: number | null;
  onSelectRoute?: (index: number) => void;
  startCoords?: [number, number];  // [lon, lat]
  endCoords?: [number, number];    // [lon, lat]
  stopCoords?: [number, number][]; // mellom-stopp [lon, lat][]
  onRemoveStop?: (index: number) => void;
  /** Copilot-utforskning: trykk på et stopp åpner plasskortet i stedet for å fjerne det.
   *  Tar forrang over onRemoveStop (fjerning skjer da via ×-knappen i RoutePlanBar). */
  onSelectStop?: (index: number) => void;
}

const ROUTE_COLORS = [BRAND, '#3b82f6', '#f59e0b'];

const RoutePlanPreview: React.FC<RoutePlanPreviewProps> = ({
  alternatives, selectedIndex, onSelectRoute,
  startCoords, endCoords, stopCoords = [], onRemoveStop, onSelectStop
}) => {
  const map = useMap();
  const prevAltRef = useRef<string>('');

  // Fit bounds when alternatives change (not on selection change)
  React.useEffect(() => {
    if (alternatives.length === 0) return;
    const altKey = alternatives.map(a => `${a.index}-${a.distanceMeters}`).join(',');
    if (altKey === prevAltRef.current) return;
    prevAltRef.current = altKey;

    const allCoords = alternatives.flatMap(a => a.coordinates);
    if (allCoords.length < 2) return;
    const lats = allCoords.map(([, lat]) => lat);
    const lons = allCoords.map(([lon]) => lon);
    const bounds = L.latLngBounds(
      [Math.min(...lats), Math.min(...lons)],
      [Math.max(...lats), Math.max(...lons)]
    );
    map.fitBounds(bounds, { padding: [60, 60], paddingBottomRight: [60, 200], maxZoom: 12 });
  }, [alternatives, map]);

  const handleFeatureEvents = useCallback((feature: any, layer: L.Layer) => {
    if (onSelectRoute && feature.properties?.index !== undefined) {
      layer.on('click', (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        onSelectRoute(feature.properties.index);
      });
    }
  }, [onSelectRoute]);

  const geoJsonLayers = useMemo(() => {
    return alternatives.map((alt, i) => {
      const isSelected = selectedIndex === i;
      const color = ROUTE_COLORS[i] || ROUTE_COLORS[0];
      const geojson: GeoJSON.Feature = {
        type: 'Feature',
        properties: { index: i },
        geometry: {
          type: 'LineString',
          coordinates: alt.coordinates,
        },
      };

      return {
        key: `route-plan-${alt.index}-${isSelected ? 's' : 'u'}-${alt.distanceMeters}`,
        data: geojson,
        style: {
          color,
          weight: isSelected ? 6 : 3,
          opacity: isSelected ? 0.9 : 0.35,
          lineCap: 'round' as const,
          lineJoin: 'round' as const,
        },
      };
    });
  }, [alternatives, selectedIndex]);

  return (
    <>
      {/* Uvalgte ruter (under) */}
      {geoJsonLayers
        .filter(l => l.data.properties?.index !== selectedIndex)
        .map(l => (
          <GeoJSON key={l.key} data={l.data} style={() => l.style} onEachFeature={handleFeatureEvents} />
        ))}
      {/* Valgt rute (over) */}
      {geoJsonLayers
        .filter(l => l.data.properties?.index === selectedIndex)
        .map(l => (
          <GeoJSON key={l.key} data={l.data} style={() => l.style} onEachFeature={handleFeatureEvents} />
        ))}

      {/* Start-markør (grønn) */}
      {startCoords && (
        <CircleMarker center={[startCoords[1], startCoords[0]]} radius={8}
          fillColor={BRAND} color="white" fillOpacity={1} weight={3} />
      )}

      {/* Stopp-markører (amber, med nummer) */}
      {stopCoords.map((sc, i) => (
        <CircleMarker key={`stop-${i}`} center={[sc[1], sc[0]]} radius={10}
          fillColor="#f59e0b" color="white" fillOpacity={1} weight={3}
          eventHandlers={onSelectStop ? {
            // Copilot: trykk åpner kortet (fjerning via ×-knappen i RoutePlanBar).
            click: (e) => { L.DomEvent.stopPropagation(e); onSelectStop(i); }
          } : onRemoveStop ? {
            click: (e) => { L.DomEvent.stopPropagation(e); onRemoveStop(i); }
          } : undefined}
        >
          <Tooltip direction="top" offset={[0, -12]} permanent className="route-stop-label">
            <span style={{ fontWeight: 800, fontSize: 10 }}>{i + 1}</span>
          </Tooltip>
        </CircleMarker>
      ))}

      {/* Slutt-markør (rød) */}
      {endCoords && (
        <CircleMarker center={[endCoords[1], endCoords[0]]} radius={8}
          fillColor="#ef4444" color="white" fillOpacity={1} weight={3} />
      )}
    </>
  );
};

export default RoutePlanPreview;
